import type * as Api from '@ViewModels';
import type { Editor, JSONContent } from '@tiptap/core';
import { EditorContent, useEditor, useEditorState } from '@tiptap/react';
import { css } from 'aphrodite';
import * as React from 'react';
import type { ISupportThreadAttachment } from '../../../../models/Support/ISupportThread';
import { formatFileSize } from '../../../../utils/Support/supportHelpers';
import { Button } from '../../../../web/components-v2/Button';
import { TabToggle } from '../../../../web/components-v2/TabToggle';
import { TextArea } from '../../../../web/components-v2/TextArea';
import { UnifiedImageDialog } from '../../../../web/components-v2/UnifiedImageDialog';
import type { ProvidedImage } from '../../../../web/components-v2/UnifiedImageDialog/types';
import { BulletListIcon, ImageIcon, OrderedListIcon } from '../TicketComposer/composerIcons';
import { keepEditorSelection } from '../TicketComposer/composerFormattingActions';
import { IssueImageMenu } from './IssueImageMenu';
import { useImageDrag } from './useImageDrag';
import {
	buildAttachmentMarkdown,
	createIssueMarkdownExtensions,
	getIssueImageReferences,
	toMarkdownLabel,
} from './issueMarkdown';
import { githubIssueEditorStyles as styles } from './styles';
import './githubIssueEditor.css';

type EditorMode = 'write' | 'markdown';

const MODE_ITEMS = [
	{ value: 'write', label: 'Write' },
	{ value: 'markdown', label: 'Markdown' },
];

interface FormatAction {
	key: string;
	title: string;
	label: React.ReactNode;
	labelStyle?: 'bold' | 'italic' | 'strike' | 'mono';
	isActive: (editor: Editor) => boolean;
	run: (editor: Editor) => void;
}

const FORMAT_ACTION_GROUPS: FormatAction[][] = [
	[
		{
			key: 'h2',
			title: 'Heading',
			label: 'H2',
			isActive: editor => editor.isActive('heading', { level: 2 }),
			run: editor => editor.chain().focus().toggleHeading({ level: 2 }).run(),
		},
		{
			key: 'h3',
			title: 'Subheading',
			label: 'H3',
			isActive: editor => editor.isActive('heading', { level: 3 }),
			run: editor => editor.chain().focus().toggleHeading({ level: 3 }).run(),
		},
	],
	[
		{
			key: 'bold',
			title: 'Bold',
			label: 'B',
			labelStyle: 'bold',
			isActive: editor => editor.isActive('bold'),
			run: editor => editor.chain().focus().toggleBold().run(),
		},
		{
			key: 'italic',
			title: 'Italic',
			label: 'I',
			labelStyle: 'italic',
			isActive: editor => editor.isActive('italic'),
			run: editor => editor.chain().focus().toggleItalic().run(),
		},
		{
			key: 'strike',
			title: 'Strikethrough',
			label: 'S',
			labelStyle: 'strike',
			isActive: editor => editor.isActive('strike'),
			run: editor => editor.chain().focus().toggleStrike().run(),
		},
		{
			key: 'code',
			title: 'Inline code',
			label: '</>',
			labelStyle: 'mono',
			isActive: editor => editor.isActive('code'),
			run: editor => editor.chain().focus().toggleCode().run(),
		},
	],
	[
		{
			key: 'bulletList',
			title: 'Bulleted list',
			label: <BulletListIcon />,
			isActive: editor => editor.isActive('bulletList'),
			run: editor => editor.chain().focus().toggleBulletList().run(),
		},
		{
			key: 'orderedList',
			title: 'Numbered list',
			label: <OrderedListIcon />,
			isActive: editor => editor.isActive('orderedList'),
			run: editor => editor.chain().focus().toggleOrderedList().run(),
		},
		{
			key: 'taskList',
			title: 'Checklist',
			label: '☐',
			isActive: editor => editor.isActive('taskList'),
			run: editor => editor.chain().focus().toggleTaskList().run(),
		},
	],
	[
		{
			key: 'blockquote',
			title: 'Quote',
			label: '❝',
			isActive: editor => editor.isActive('blockquote'),
			run: editor => editor.chain().focus().toggleBlockquote().run(),
		},
		{
			key: 'codeBlock',
			title: 'Code block',
			label: '{ }',
			labelStyle: 'mono',
			isActive: editor => editor.isActive('codeBlock'),
			run: editor => editor.chain().focus().toggleCodeBlock().run(),
		},
	],
];

const FORMAT_ACTIONS = FORMAT_ACTION_GROUPS.flat();

const LABEL_STYLES = {
	bold: styles.labelBold,
	italic: styles.labelItalic,
	strike: styles.labelStrike,
	mono: styles.labelMono,
};

const RECEIVED_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
	month: 'short',
	day: 'numeric',
	hour: 'numeric',
	minute: '2-digit',
});

export interface TicketImage {
	attachment: ISupportThreadAttachment;
	/** ISO time of the thread that carried the image. */
	receivedAt: string;
}

const toProvidedImage = ({ attachment, receivedAt }: TicketImage, usedIds: ReadonlySet<string>): ProvidedImage => {
	const received = new Date(receivedAt);
	const date = Number.isNaN(received.getTime()) ? null : RECEIVED_DATE_FORMAT.format(received);
	return {
		attachment: { ...attachment, fileSize: attachment.fileSize ?? 0 },
		detail: [date, formatFileSize(attachment.fileSize)].filter(Boolean).join(' · '),
		badge: usedIds.has(attachment.id) ? 'In issue' : undefined,
	};
};

/**
 * A ticket image is written as an attachment reference the API publishes on issue creation; an upload
 * is already a public content image, so its own URL goes straight into the markdown.
 */
const buildImageContent = (image: Api.IFileAttachment, isTicketImage: boolean): JSONContent => ({
	type: 'image',
	attrs: {
		src: image.url,
		alt: toMarkdownLabel(image.fileName),
		attachmentId: isTicketImage ? image.id : null,
	},
});

const buildImageMarkdown = (image: Api.IFileAttachment, isTicketImage: boolean) =>
	isTicketImage ? buildAttachmentMarkdown(image, 'Inline') : `![${toMarkdownLabel(image.fileName)}](${image.url})`;

export interface GitHubIssueBodyEditorProps {
	value: string;
	onChange: (markdown: string) => void;
	/** Newest first; the picker lists them in this order. */
	images: TicketImage[];
	disabled?: boolean;
}

export const GitHubIssueBodyEditor: React.FC<GitHubIssueBodyEditorProps> = ({ value, onChange, images, disabled }) => {
	const [mode, setMode] = React.useState<EditorMode>('write');
	const [pickerOpen, setPickerOpen] = React.useState(false);
	const frameRef = React.useRef<HTMLDivElement>(null);
	const [surface, setSurface] = React.useState<HTMLDivElement | null>(null);
	const sourceRef = React.useRef<HTMLTextAreaElement>(null);
	const sourceSelectionRef = React.useRef<{ start: number; end: number } | null>(null);
	const imagesByIdRef = React.useRef(new Map<string, ISupportThreadAttachment>());
	imagesByIdRef.current = new Map(images.map(image => [image.attachment.id, image.attachment]));
	const onChangeRef = React.useRef(onChange);
	onChangeRef.current = onChange;

	const editor = useEditor({
		extensions: createIssueMarkdownExtensions({
			getImageUrl: attachmentId => imagesByIdRef.current.get(attachmentId)?.url,
			placeholder: 'Describe the issue. Markdown shortcuts like ## and - work as you type.',
		}),
		content: value,
		contentType: 'markdown',
		editable: !disabled,
		editorProps: { attributes: { class: 'gh-issue-editor-content', 'aria-label': 'Issue body' } },
		onUpdate: ({ editor: current }) => onChangeRef.current(current.getMarkdown()),
		shouldRerenderOnTransaction: false,
	});

	React.useEffect(() => {
		editor?.setEditable(!disabled);
	}, [editor, disabled]);

	const dropIndicatorTop = useImageDrag(editor, surface, mode === 'write' && !disabled);

	// A draft can reach the editor before the ticket's attachments load; fill in each image once they do.
	const imageUrlsKey = images.map(image => `${image.attachment.id}=${image.attachment.url ?? ''}`).join('|');
	React.useEffect(() => {
		if (!editor || !imageUrlsKey) return;
		const transaction = editor.state.tr;
		editor.state.doc.descendants((node, pos) => {
			const attachmentId = node.type.name === 'image' ? (node.attrs.attachmentId as string | null) : null;
			const url = attachmentId ? imagesByIdRef.current.get(attachmentId)?.url : undefined;
			if (url && node.attrs.src !== url) transaction.setNodeMarkup(pos, undefined, { ...node.attrs, src: url });
		});
		if (transaction.docChanged) editor.view.dispatch(transaction.setMeta('addToHistory', false));
	}, [editor, imageUrlsKey]);

	const activeFormats = useEditorState({
		editor,
		selector: ({ editor: current }) =>
			Object.fromEntries(FORMAT_ACTIONS.map(action => [action.key, current ? action.isActive(current) : false])),
	});

	const handleModeChange = (next: string) => {
		const nextMode = next as EditorMode;
		if (nextMode === mode) return;
		if (nextMode === 'write' && editor) {
			editor.commands.setContent(value, { contentType: 'markdown', emitUpdate: false });
		}
		setMode(nextMode);
	};

	const usedIds = new Set(getIssueImageReferences(value).map(reference => reference.attachmentId));

	const openPicker = () => {
		const source = sourceRef.current;
		// The dialog takes focus, so the source cursor is captured now; ProseMirror keeps its own selection.
		sourceSelectionRef.current =
			source && document.activeElement === source ? { start: source.selectionStart, end: source.selectionEnd } : null;
		setPickerOpen(true);
	};

	const insertIntoSource = (markdown: string) => {
		const selection = sourceSelectionRef.current ?? { start: value.length, end: value.length };
		const needsBreak = selection.start > 0 && value[selection.start - 1] !== '\n';
		const inserted = `${needsBreak ? '\n\n' : ''}${markdown}\n`;
		onChange(value.slice(0, selection.start) + inserted + value.slice(selection.end));
		const cursor = selection.start + inserted.length;
		requestAnimationFrame(() => {
			sourceRef.current?.focus();
			sourceRef.current?.setSelectionRange(cursor, cursor);
		});
	};

	const insertImage = (image: Api.IFileAttachment) => {
		if (!image.url) return;
		const isTicketImage = imagesByIdRef.current.has(image.id);
		if (mode === 'markdown') {
			insertIntoSource(buildImageMarkdown(image, isTicketImage));
			return;
		}
		editor?.chain().focus().insertContent(buildImageContent(image, isTicketImage)).run();
	};

	return (
		<div ref={frameRef} className={css(styles.frame, disabled && styles.frameDisabled)}>
			<div className={css(styles.toolbar)}>
				<div className={css(styles.toolbarActions)}>
					<div className={css(styles.toolbarFormatActions, mode === 'markdown' && styles.toolbarActionsHidden)}>
						{FORMAT_ACTION_GROUPS.map((group, groupIndex) => (
							<React.Fragment key={group[0].key}>
								{groupIndex > 0 && <span className={css(styles.toolbarDivider)} aria-hidden='true' />}
								{group.map(action => (
									<Button
										key={action.key}
										variant='ghost'
										size='sm'
										styles={[
											styles.toolbarButton,
											action.labelStyle && LABEL_STYLES[action.labelStyle],
											activeFormats?.[action.key] && styles.toolbarButtonActive,
										]}
										onMouseDown={keepEditorSelection}
										onClick={() => editor && action.run(editor)}
										disabled={disabled || !editor}
										title={action.title}
										aria-label={action.title}
										aria-pressed={!!activeFormats?.[action.key]}
									>
										{action.label}
									</Button>
								))}
							</React.Fragment>
						))}
					</div>
					<span className={css(styles.toolbarDivider)} aria-hidden='true' />
					<Button
						variant='ghost'
						size='sm'
						styles={[styles.toolbarButton, styles.insertButton]}
						onMouseDown={keepEditorSelection}
						onClick={openPicker}
						disabled={disabled || !editor}
					>
						<ImageIcon />
						Insert attachment
					</Button>
				</div>
				<TabToggle
					items={MODE_ITEMS}
					value={mode}
					onValueChange={handleModeChange}
					size='small'
					aria-label='Editor mode'
					styles={[styles.modeToggle]}
				/>
			</div>
			<div ref={setSurface} className={css(styles.surface, mode === 'markdown' && styles.hidden)}>
				<EditorContent editor={editor} />
				{dropIndicatorTop === null ? null : (
					<div className={css(styles.dropIndicator)} style={{ top: dropIndicatorTop }} aria-hidden='true' />
				)}
			</div>
			{editor && mode === 'write' && !disabled ? (
				<IssueImageMenu editor={editor} containerRef={frameRef} scrollTarget={surface} />
			) : null}
			{mode === 'markdown' && (
				<TextArea
					ref={sourceRef}
					aria-label='Issue body markdown'
					styles={[styles.source]}
					value={value}
					onChange={event => onChange(event.target.value)}
					disabled={disabled}
					spellCheck={false}
				/>
			)}
			{pickerOpen ? (
				<UnifiedImageDialog
					open
					onOpenChange={setPickerOpen}
					initialTab={images.length > 0 ? 'provided-images' : 'upload'}
					onImageSelected={insertImage}
					providedImages={images.map(image => toProvidedImage(image, usedIds))}
					providedImagesLabel='Ticket images'
					providedImagesEmptyText='This ticket has no images yet.'
					showFreeImagesTab={false}
					showAIGenerateTab={false}
					allowAIEdit={false}
				/>
			) : null}
		</div>
	);
};
